import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';

import {
  getErrorMessage,
  invalidateDxfSceneMappingQuery,
  PartWorkerTasks,
  TaskResolution,
  TaskStatus,
  type DxfSceneMappingWriteVariables,
  type DxfSplitResult,
  type DxfSplitCompletedMeta,
  type DxfSplitTaskMeta,
  type DxfSplitVariables,
  isValidAssignments,
  useCreateDxfSceneMapping,
  useSplitDxfPartFile,
  useUpdateDxfSceneMapping,
} from '@frontend/api';
import { useToast } from '@frontend/legacy-components';

import {
  createEntityAction,
  getReasonText,
} from 'modules/common/entityActions';
import {
  DXF_MAPPING_SAVE_BLOCK,
  DXF_MAPPING_SPLIT_BLOCK,
  DxfMappingActionsEntity,
  dxfMappingEditDefinition,
  dxfMappingSaveDefinition,
  dxfMappingSplitToLineItemsDefinition,
  getDxfMappingEditAvailability,
  type DxfMappingActionContext,
  type DxfMappingActions,
  type DxfMappingQuoteToSplitTo,
  unavailableQuoteToSplitTo,
} from 'modules/common/viewer/dxfViewer/mapping/mappingActions';
import { useViewerAccess } from 'modules/common/viewer/hostContracts/access';
import { useViewerEditLocks } from 'modules/common/viewer/hostContracts/editLocks';

import { useTaskStatusTracking } from 'utils/hooks/taskTracking/useTaskStatusTracking';
import { pluralize } from 'utils/stringUtils';

import {
  toAssignmentsMap,
  toAssignmentsPayload,
} from '../session/mappingModel';
import type { MappingAssignments, DxfViewerSession } from '../session/types';
import { useReadableStore } from '../session/useSessionState';

const NO_DRAWING_LOADED = 'No drawing loaded';

// For pages that act in no quote.
const NO_QUOTE_TO_SPLIT_TO = unavailableQuoteToSplitTo();

const MAPPING_WRITE_OPTIONS = {
  queryOptions: {
    onSettled: (
      _data: unknown,
      _error: unknown,
      { partFileUuid }: DxfSceneMappingWriteVariables,
    ) => invalidateDxfSceneMappingQuery(partFileUuid),
  },
};

const SPLIT_OPTIONS = {
  queryOptions: {
    // The server saved the mapping before splitting.
    onSettled: (
      _data: unknown,
      _error: unknown,
      { partFileUuid }: DxfSplitVariables,
    ) => invalidateDxfSceneMappingQuery(partFileUuid),
  },
};

/**
 * Composition root for the mapping's entity actions.
 */
export const DxfMappingActionsRoot = ({
  session,
  onMappingSaved,
  quoteToSplitTo = NO_QUOTE_TO_SPLIT_TO,
  onSplitStarted,
  onSplitFinished,
  children,
}: {
  session: DxfViewerSession;
  /** The page's reaction to a saved mapping (it re-measures the part). */
  onMappingSaved?: () => void;
  /**
   * Where a split's line items land, from the host page; unavailable (the
   * default) for pages that act in no quote.
   */
  quoteToSplitTo?: DxfMappingQuoteToSplitTo;
  /**
   * The page's side effects once a split task has been created (not when it
   * finishes), e.g. refreshing what the split will change.
   */
  onSplitStarted?: () => void;
  /**
   * The page's side effects once a split task of this file finishes, whichever
   * tab or user started it: the new line items exist only from here.
   */
  onSplitFinished?: (result: DxfSplitResult) => void;
  children: ReactNode;
}) => {
  const toast = useToast();
  const access = useViewerAccess();
  const editLocks = useViewerEditLocks();
  const hasModel = useReadableStore(session.model, (model) => model !== null);
  const createMapping = useCreateDxfSceneMapping(MAPPING_WRITE_OPTIONS);
  const updateMapping = useUpdateDxfSceneMapping(MAPPING_WRITE_OPTIONS);
  const isSaving = createMapping.isPending || updateMapping.isPending;
  const splitPartFile = useSplitDxfPartFile(SPLIT_OPTIONS);
  const isSplitting = splitPartFile.isPending;
  const partFileUuid = useReadableStore(session.partFileUuid);
  const { registerCallback, unregisterCallback } = useTaskStatusTracking();
  // The kickoff toast of each split started here, by part file, so its outcome
  // can update it.
  const splitToastIdsRef = useRef<Record<string, ReturnType<typeof toast>>>({});

  const ctx = useMemo<DxfMappingActionContext>(
    () => ({ access, editLocks, quoteToSplitTo }),
    [access, editLocks, quoteToSplitTo],
  );

  /**
   * The draft as a write payload, or `null` (with a toast) when it is malformed.
   * Pre-flighted with the rule the write validator applies, so a bad draft is
   * caught here rather than as a failed round trip.
   */
  const toValidPayload = useCallback(
    (draft: MappingAssignments) => {
      const assignments = toAssignmentsPayload(draft);
      if (isValidAssignments(assignments)) {
        return assignments;
      }
      toast({
        title: 'Invalid mapping structure',
        description: 'Please check your mapping and try again.',
        status: 'error',
      });
      return null;
    },
    [toast],
  );

  // A split finishes in a task; its final status message is the only signal.
  useEffect(() => {
    if (!partFileUuid) {
      return undefined;
    }
    const callbackKey = `dxf_split_${partFileUuid}`;
    registerCallback(callbackKey, ({ updateMessages }) => {
      for (const update of updateMessages) {
        if (
          update.task === PartWorkerTasks.DXF_SPLIT &&
          update.entityKey === partFileUuid &&
          update.status === TaskStatus.COMPLETED
        ) {
          const result: DxfSplitResult =
            update.resolution === TaskResolution.SUCCESS
              ? {
                  succeeded: true,
                  meta: update.meta as DxfSplitCompletedMeta,
                }
              : {
                  succeeded: false,
                  meta: update.meta as DxfSplitTaskMeta | undefined,
                };
          const outcome = result.succeeded
            ? {
                title: `Split into ${pluralize(
                  'line item',
                  result.meta.lineItemCount,
                )}`,
                status: 'success' as const,
              }
            : {
                title: "Couldn't split into line items",
                description: 'If this issue persists, please contact support.',
                status: 'error' as const,
              };
          // A split started elsewhere, or before a reload, has no toast here
          // yet; it still gets its outcome.
          const toastId = splitToastIdsRef.current[partFileUuid];
          if (toastId !== undefined) {
            toast.update(toastId, {
              ...outcome,
              duration: 5000,
              isClosable: true,
            });
            delete splitToastIdsRef.current[partFileUuid];
          } else {
            toast(outcome);
          }
          onSplitFinished?.(result);
        }
      }
    });
    return () => unregisterCallback(callbackKey);
  }, [
    partFileUuid,
    registerCallback,
    unregisterCallback,
    onSplitFinished,
    toast,
  ]);

  // The kickoff toast can't be dismissed and only the task's final message
  // resolves it, which this file stops listening for once the viewer leaves it.
  useEffect(() => {
    if (!partFileUuid) {
      return undefined;
    }
    const splitToastIds = splitToastIdsRef.current;
    return () => {
      const toastId = splitToastIds[partFileUuid];
      if (toastId !== undefined) {
        toast.close(toastId);
        delete splitToastIds[partFileUuid];
      }
    };
  }, [partFileUuid, toast]);

  useEffect(() => {
    const availability = getDxfMappingEditAvailability(ctx);
    session.mapping.setLockReason(
      availability.status === 'enabled'
        ? undefined
        : getReasonText(availability.reason) ?? 'Mapping is unavailable',
    );
  }, [session, ctx]);

  const actions = useMemo<DxfMappingActions>(
    () => ({
      edit: createEntityAction(dxfMappingEditDefinition, ctx, {
        execute: () => session.mode.set('mapping'),
        getTransientReason: () => (hasModel ? undefined : NO_DRAWING_LOADED),
      }),
      save: createEntityAction(dxfMappingSaveDefinition, ctx, {
        execute: ([mapping]) => {
          const { draft, saved } = session.mapping.getState();
          if (!mapping || !draft) {
            return;
          }

          const assignments = toValidPayload(draft);
          if (!assignments) {
            return;
          }

          // The backend refuses a second POST, so the verb follows `saved`.
          const mutation = saved === null ? createMapping : updateMapping;
          mutation.mutate(
            { partFileUuid: mapping.partFileUuid, assignments },
            {
              onSuccess: (result) => {
                // The server's copy, not the draft: what the session believes is
                // saved should be what was actually stored.
                session.mapping.commitSaved(
                  toAssignmentsMap(result.assignments),
                );
                session.mode.set('view');
                onMappingSaved?.();
              },
              onError: (error) => {
                console.error(error);
                // The server says why it refused, such as the mapping being
                // locked by a nest, and that is what someone needs to act on.
                toast({
                  title: 'Failed to save mapping',
                  description: getErrorMessage(error),
                  status: 'error',
                });
              },
            },
          );
        },
        // The split request saves the mapping too.
        getIsPending: () => isSaving || isSplitting,
        // A check in flight might come back with new issues.
        getTransientReason: ([mapping]) =>
          mapping?.validation.status === 'pending'
            ? DXF_MAPPING_SAVE_BLOCK.PENDING
            : undefined,
      }),
      splitToLineItems: createEntityAction(
        dxfMappingSplitToLineItemsDefinition,
        ctx,
        {
          execute: ([mapping]) => {
            const { draft } = session.mapping.getState();
            if (
              !mapping ||
              !draft ||
              ctx.quoteToSplitTo.status !== 'available'
            ) {
              return;
            }
            const assignments = toValidPayload(draft);
            if (!assignments) {
              return;
            }

            splitPartFile.mutate(
              {
                partFileUuid: mapping.partFileUuid,
                assignments,
                quoteId: ctx.quoteToSplitTo.quote.id,
              },
              {
                onSuccess: () => {
                  // The server saved this draft before splitting by it. Keep it
                  // locally too, invalid as it is, and leave mapping: the file is
                  // being split rather than mapped as one part.
                  session.mapping.commitSaved(draft);
                  session.mode.set('view');
                  // Stays up until the task finishes and updates it.
                  splitToastIdsRef.current[mapping.partFileUuid] = toast({
                    title: 'Splitting into line items…',
                    status: 'info',
                    duration: null,
                    isClosable: false,
                  });
                  onSplitStarted?.();
                },
                onError: (error) => {
                  console.error(error);
                  // Stay in mapping: nothing was split, and the draft is intact.
                  toast({
                    title: 'Failed to split into line items',
                    description: getErrorMessage(error),
                    status: 'error',
                  });
                },
              },
            );
          },
          getIsPending: () => isSplitting,
          getTransientReason: ([mapping]) =>
            mapping?.validation.status === 'pending'
              ? DXF_MAPPING_SPLIT_BLOCK.PENDING
              : undefined,
        },
      ),
    }),
    [
      ctx,
      session,
      hasModel,
      toast,
      toValidPayload,
      createMapping,
      updateMapping,
      isSaving,
      onMappingSaved,
      splitPartFile,
      isSplitting,
      onSplitStarted,
    ],
  );

  return (
    <DxfMappingActionsEntity.Provider actions={actions}>
      {children}
    </DxfMappingActionsEntity.Provider>
  );
};
